import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Computer, Cloud, Users, Cpu, 
  Network, Globe, Grid, MoreHorizontal 
} from 'lucide-react';
import { Base_Url } from '../config';
import { useParams, useNavigate } from 'react-router-dom';


const EditAdvancedServiceCard = () => {
  const navigate = useNavigate();
  const { cardId } = useParams();
  
  const [serviceData, setServiceData] = useState({
    title: '',
    items: [{ name: '', link: '' }],
    externalLink: '',
    parentpath: '',
    card_desc: '',
    cardpath: '',
    icon: '',
    author:'',
    isHighlight: false,
    description: ''
  });

  const [existingRoutes, setExistingRoutes] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [searchTerms, setSearchTerms] = useState({
    parentpath: '',
    cardpath: '',
    items: Array(serviceData.items.length).fill('')
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState({
    parentpath: false,
    cardpath: false,
    items: []
  });

  const dropdownRefs = useRef({
    parentpath: null,
    cardpath: null,
    items: []
  });

  // Fetch card data
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        const response = await axios.get(`${Base_Url}/cardsinfo/${cardId}`);
        
        // Parse the card_details JSON if it's a string
        let cardDetails;
        if (typeof response.data.card_details === 'string') {
          cardDetails = JSON.parse(response.data.card_details);
        } else {
          cardDetails = response.data.card_details;
        }

        // Set the service data
        setServiceData({
          title: cardDetails.title || '',
          items: cardDetails.items || [{ name: '', link: '' }],
          externalLink: cardDetails.externalLink || '',
          parentpath: cardDetails.parentpath || '',
          cardpath: cardDetails.cardpath || '',
          card_desc: cardDetails.card_desc || '',
          author: cardDetails.author || '',
          icon: cardDetails.icon || '<RiComputerFill />',
          isHighlight: cardDetails.isHighlight || false,
          description: cardDetails.description || ''
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch card data', err);
        setError('Failed to load card data');
        setLoading(false);
      }
    };

    if (cardId) {
      fetchCardData();
    }
  }, [cardId]);

  // Fetch all routes
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await axios.get(`${Base_Url}/createroutes/${cardId}`);
        setAllRoutes(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch routes', err);
        setAllRoutes([]);
      }
    };
    fetchRoutes();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRefs.current.parentpath && 
          !dropdownRefs.current.parentpath.contains(event.target)) {
        setIsDropdownOpen(prev => ({
          ...prev,
          parentpath: false
        }));
      }

      if (dropdownRefs.current.cardpath && 
          !dropdownRefs.current.cardpath.contains(event.target)) {
        setIsDropdownOpen(prev => ({
          ...prev,
          cardpath: false
        }));
      }

      dropdownRefs.current.items.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          setIsDropdownOpen(prev => {
            const newItemDropdowns = [...prev.items];
            newItemDropdowns[index] = false;
            return { ...prev, items: newItemDropdowns };
          });
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [serviceData.items.length]);

  const addItem = () => {
    setServiceData(prevState => {
      const newItems = [...prevState.items, { name: '', link: '' }];
      
      setSearchTerms(prev => ({
        ...prev,
        items: [...prev.items, '']
      }));

      setIsDropdownOpen(prev => ({
        ...prev,
        items: [...prev.items, false]
      }));

      return {
        ...prevState,
        items: newItems
      };
    });
  };

  const removeItem = (index) => {
    setServiceData(prevState => {
      const newItems = prevState.items.filter((_, i) => i !== index);
      
      const newSearchTerms = searchTerms.items.filter((_, i) => i !== index);
      const newDropdownState = isDropdownOpen.items.filter((_, i) => i !== index);

      setSearchTerms(prev => ({
        ...prev,
        items: newSearchTerms
      }));

      setIsDropdownOpen(prev => ({
        ...prev,
        items: newDropdownState
      }));

      return {
        ...prevState,
        items: newItems
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setServiceData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (['parentpath', 'cardpath'].includes(name)) {
      setSearchTerms(prev => ({
        ...prev,
        [name]: value
      }));
      setIsDropdownOpen(prev => ({
        ...prev,
        [name]: true
      }));
    }
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    setServiceData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [name]: value };
      return { ...prev, items: newItems };
    });

    if (name === 'link') {
      setSearchTerms(prev => {
        const newItemSearchTerms = [...prev.items];
        newItemSearchTerms[index] = value;
        return { ...prev, items: newItemSearchTerms };
      });

      setIsDropdownOpen(prev => {
        const newItemDropdowns = [...prev.items];
        newItemDropdowns[index] = true;
        return { ...prev, items: newItemDropdowns };
      });
    }
  };

  const getAvailableRoutes = (type, index = null) => {
    const selectedRoutes = [
      serviceData.parentpath, 
      serviceData.cardpath, 
      ...serviceData.items.map(item => item.link)
    ].filter(route => route !== '');

    const availableRoutes = allRoutes.filter(route => 
      !selectedRoutes.includes(route.routes)
    );

    let filteredRoutes = availableRoutes;
    if (type === 'parentpath' && searchTerms.parentpath) {
      filteredRoutes = filteredRoutes.filter(route => 
        route.routes.toLowerCase().includes(searchTerms.parentpath.toLowerCase())
      );
    } else if (type === 'cardpath' && searchTerms.cardpath) {
      filteredRoutes = filteredRoutes.filter(route => 
        route.routes.toLowerCase().includes(searchTerms.cardpath.toLowerCase())
      );
    } else if (type === 'itemLink' && index !== null && searchTerms.items[index]) {
      filteredRoutes = filteredRoutes.filter(route => 
        route.routes.toLowerCase().includes(searchTerms.items[index].toLowerCase())
      );
    }

    return filteredRoutes.slice(0, 5);
  };

  const handleRouteSelect = (route, type, itemIndex = null) => {
    if (type === 'parentpath') {
      setServiceData(prev => ({
        ...prev,
        parentpath: route.routes
      }));
      setIsDropdownOpen(prev => ({
        ...prev,
        parentpath: false
      }));
    } else if (type === 'cardpath') {
      setServiceData(prev => ({
        ...prev,
        cardpath: route.routes
      }));
      setIsDropdownOpen(prev => ({
        ...prev,
        cardpath: false
      }));
    } else if (type === 'itemLink' && itemIndex !== null) {
      setServiceData(prev => {
        const newItems = [...prev.items];
        newItems[itemIndex].link = route.routes;
        return { ...prev, items: newItems };
      });
      setIsDropdownOpen(prev => {
        const newItemDropdowns = [...prev.items];
        newItemDropdowns[itemIndex] = false;
        return { ...prev, items: newItemDropdowns };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
  
    const validationErrors = [];
  
    if (!serviceData.title || serviceData.title.trim() === '') {
      validationErrors.push('Card Title is required');
    }
  
    if (!serviceData.parentpath || serviceData.parentpath.trim() === '') {
      validationErrors.push('Parent Route is required');
    }
  
    // if (!serviceData.cardpath || serviceData.cardpath.trim() === '') {
    //   validationErrors.push('Card Route is required');
    // }
  
    if (serviceData.items && serviceData.items.length > 0) {
      serviceData.items.forEach((item, index) => {
        if ((item.name && !item.link) || (!item.name && item.link)) {
          validationErrors.push(`Item ${index + 1}: Both Name and Link must be provided if one is filled`);
        }
      });
    }
  
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }
  
    try {
        const submissionData = {
          card_details: {
            title: serviceData.title.trim(),
            items: serviceData.items.map(item => ({
              name: item.name.trim(),
              link: item.link.trim()
            })),
            externalLink: serviceData.externalLink ? serviceData.externalLink.trim() : '',
            parentpath: serviceData.parentpath.trim(),
            cardpath: serviceData.cardpath.trim(),
            card_desc: serviceData.card_desc || '',
            author: serviceData.author || '',
            icon: serviceData.icon,
            isHighlight: serviceData.isHighlight,
            description: serviceData.description || ''
          }
        };
    
        const response = await axios.put(`${Base_Url}/cardsinfo/${cardId}`, submissionData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
    
        if (response.status === 200) {
          setSuccess('Card updated successfully!');
          navigate('/services'); // Adjust navigation path as needed
        } else {
          setError('Unexpected response from server');
        }
      } catch (err) {
      console.error('Update Error:', err);
  
      let errorMessage = 'Failed to update service';
  
      if (err.response) {
        console.error('Error Response Data:', err.response.data);
        console.error('Error Response Status:', err.response.status);
        
        errorMessage = err.response.data.message || 
                       err.response.data.error || 
                       `Server error: ${err.response.status}`;
      } else if (err.request) {
        console.error('No response received:', err.request);
        errorMessage = 'No response from server. Please check your network connection.';
      } else {
        console.error('Error setting up request:', err.message);
        errorMessage = `Request setup error: ${err.message}`;
      }
  
      setError(errorMessage);
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h2 className="text-center">Edit Card</h2>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success" role="alert">
                  {success}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Card Title</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="title"
                    placeholder='Your Card Name'
                    value={serviceData.title}
                    onChange={handleChange}
                    required 
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Service Items</label>
                  {serviceData.items.map((item, index) => (
                    <div key={index} className="row mb-2 align-items-center">
                      <div className="col-md-5">
                        <input 
                          type="text" 
                          className="form-control" 
                          name="name"
                          placeholder="Item Name"
                          value={item.name}
                          onChange={(e) => handleItemChange(index, e)}
                        />
                      </div>
                      <div className="col-md-5 position-relative">
                        <input 
                          type="text" 
                          className="form-control" 
                          name="link"
                          placeholder="Item Link"
                          value={item.link}
                          onChange={(e) => handleItemChange(index, e)}
                          autoComplete="off"
                        />
                        {isDropdownOpen.items[index] && (
                          <div className="position-absolute bg-white border rounded w-100 shadow-sm" 
                               style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                            {getAvailableRoutes('itemLink', index).map((route, routeIndex) => (
                              <div 
                                key={routeIndex} 
                                className="dropdown-item p-2 hover-bg-light"
                                onClick={() => handleRouteSelect(route, 'itemLink', index)}
                                style={{ cursor: 'pointer' }}
                              >
                                {route.routes}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="col-md-2">
                        {index > 0 && (
                          <button 
                            type="button"className="btn btn-danger" 
                            onClick={() => removeItem(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-secondary mt-2" 
                    onClick={addItem}
                  >
                    Add Item
                  </button>
                </div>

                <div className="mb-3">
                  <label className="form-label">External Link (Optional)</label>
                  <input 
                    type="url" 
                    className="form-control" 
                    name="externalLink"
                    value={serviceData.externalLink}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Card Description (Optional)</label>
                  <textarea 
                    className="form-control" 
                    name="card_desc"
                    value={serviceData.card_desc}
                    onChange={handleChange}
                    placeholder="Enter Card Description"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Author</label>
                  <textarea 
                    className="form-control" 
                    name="author"
                    value={serviceData.author}
                    onChange={handleChange}
                    placeholder="Enter Card Description"
                  />
                </div>

                <div className="mb-3 position-relative">
                  <label className="form-label">Parent Route</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="parentpath"
                    value={serviceData.parentpath}
                    onChange={handleChange}
                    placeholder="/parent/your-childroute"
                    required 
                    autoComplete="off"
                  />
                  {isDropdownOpen.parentpath && (
                    <div 
                      className="position-absolute bg-white border rounded w-100 shadow-sm" 
                      style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                      ref={dropdownRefs.current.parentpath}
                    >
                      {getAvailableRoutes('parentpath').map((route, index) => (
                        <div 
                          key={index} 
                          className="dropdown-item p-2 hover-bg-light"
                          onClick={() => handleRouteSelect(route, 'parentpath')}
                          style={{ cursor: 'pointer' }}
                        >
                          {route.routes}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-3 position-relative">
                  <label className="form-label">Card Route</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="cardpath"
                    value={serviceData.cardpath}
                    onChange={handleChange}
                    placeholder="/thiscardroute"
                    // required 
                    autoComplete="off"
                  />
                  {isDropdownOpen.cardpath && (
                    <div 
                      className="position-absolute bg-white border rounded w-100 shadow-sm" 
                      style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                      ref={dropdownRefs.current.cardpath}
                    >
                      {getAvailableRoutes('cardpath').map((route, index) => (
                        <div 
                          key={index} 
                          className="dropdown-item p-2 hover-bg-light"
                          onClick={() => handleRouteSelect(route, 'cardpath')}
                          style={{ cursor: 'pointer' }}
                        >
                          {route.routes}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Icon</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="icon"
                    value={serviceData.icon}
                    onChange={handleChange}
                      placeholder="Follow this link to get icons: https://lucide.dev/icons/ and copy the JSX code, then paste it here."
                    required 
                    autoComplete="off"
                  />
                </div>
                <a href="https://lucide.dev/icons/" target="_blank">
  <button type="button" class="btn btn">
    <span class="badge badge-light">Follow this link to get icons: https://lucide.dev/icons/ and copy the JSX code, then paste it here.</span>
  </button>
</a>

                <div className="text-center">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg"
                    disabled={loading}
                  >
                    Update Card
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-lg ms-2"
                    onClick={() => navigate('/services')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAdvancedServiceCard;